import { AxiosError } from 'axios'

export const getErrorMessage = (error: unknown, fallback: string) => {
  if (typeof error === 'string' && error) {
    return error
  }

  const responseData = (error as AxiosError<{ message?: string; error?: string } | string>)
    ?.response?.data

  if (typeof responseData === 'string' && responseData) {
    return responseData
  }

  if (responseData && typeof responseData === 'object') {
    if (responseData.message) return responseData.message
    if (responseData.error) return responseData.error
  }

  if (error && typeof error === 'object') {
    const directMessage = 'message' in error ? error.message : undefined
    if (typeof directMessage === 'string' && directMessage) return directMessage

    const directError = 'error' in error ? error.error : undefined
    if (typeof directError === 'string' && directError) return directError
  }

  return fallback
}

export type AcceleratedRentStepUp = {
  leaseAmount: number
  days: number
  periodStartDate: string
  periodEndDate: string
}

export type AcceleratedRentMonthlyBreakdown = {
  accrualMonth: string
  periodStartDate: string
  periodEndDate: string
  acceleratedDays: number
  daysInMonth: number
  stepUps: AcceleratedRentStepUp[]
  expectedAcceleratedRent: number
}

export type AcceleratedRentCalculation = {
  rentalId: string
  moveOutDate: string | null
  finalLiabilityDate: string | null
  hasFinalLiabilityAfterMoveOut: boolean
  acceleratedStartDate: string | null
  acceleratedEndDate: string | null
  nextRentalOccupancyDate: string | null
  monthlyBreakdown: AcceleratedRentMonthlyBreakdown[]
  expectedAcceleratedRentTotal: number
  auditTotalRounded: number
  auditTotalUnrounded: number
}

export type AcceleratedRentEntry = {
  id: string
  date: string
  amount: number
  note: string | null
  liabilityTypeId: string
  liabilityLabel: string
  type: 'Charge'
  status: ''
}

export type AcceleratedRentExpectedRow = AcceleratedRentMonthlyBreakdown & {
  status: 'missing' | 'matching' | 'mismatched' | 'duplicate'
  entries: AcceleratedRentEntry[]
}

export type AcceleratedRentUnmatchedEntry = AcceleratedRentEntry & {
  classification: 'stale' | 'manual' | 'unclassified'
  accrualMonth?: string
}

export type AcceleratedRentPostingIssue = {
  code:
    | 'NO_ACCELERATED_WINDOW'
    | 'AGREEMENT_GAP'
    | 'UNABLE_TO_DETERMINE_RENT'
    | 'DUPLICATE_ENTRIES'
    | 'STALE_ENTRIES'
    | 'MANUAL_ENTRIES'
    | 'MISMATCHED_ENTRIES'
    | 'AWAITING_REGULAR_RENT'
    | 'REGULAR_RENT_MISMATCH'
    | 'LEDGER_IMMUTABLE'
  severity: 'info' | 'requires_acknowledgement' | 'blocking'
  message: string
}

export type RegularRentCheck =
  | { ok: true }
  | {
      ok: false
      kind: 'awaiting_generation'
      moveOutMonth: string
      expectedRegularRent: number
      regularRentRowsFound: 0
    }
  | {
      ok: false
      kind: 'mismatch'
      moveOutMonth: string
      expected: number
      actual: number
      tolerance: number
      finalMonthRegularRentRows: Array<
        Pick<AcceleratedRentEntry, 'id' | 'date' | 'amount' | 'note'>
      >
      postMoveOutRegularRentRows: Array<
        Pick<AcceleratedRentEntry, 'id' | 'date' | 'amount' | 'note'>
      >
    }

export type AcceleratedRentPreview = {
  rentalId: string
  calculation: AcceleratedRentCalculation | null
  automaticAttemptedAt: string | null
  automaticOutcome:
    | 'posted'
    | 'already_matching'
    | 'no_charge'
    | 'review_required'
    | 'awaiting_data'
    | 'skipped_ineligible'
    | null
  postingCompletedAt: string | null
  expectedRows: AcceleratedRentExpectedRow[]
  unmatchedEntries: AcceleratedRentUnmatchedEntry[]
  regularRentCheck: RegularRentCheck
  postingIssues: AcceleratedRentPostingIssue[]
  defaultPostingDate: string | null
  totalToPost: number
}

export type CreatedAcceleratedRentRow = {
  id: string
  rentalId: string
  date: string
  amount: number
  accrualMonth: string
  note: string
}

export type AcceleratedRentPostResponse =
  | {
      result: 'posted'
      created: CreatedAcceleratedRentRow[]
      skippedMonths: string[]
      totalPosted: number
    }
  | { result: string }

export const getLeaseSummary = (stepUps: AcceleratedRentStepUp[]) => {
  if (stepUps.length === 0) {
    return '-'
  }

  if (stepUps.length === 1) {
    return `$${stepUps[0].leaseAmount.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    })}`
  }

  return stepUps
    .map(
      (stepUp) =>
        `$${stepUp.leaseAmount.toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2
        })} for ${stepUp.days} ${stepUp.days === 1 ? 'day' : 'days'}`
    )
    .join(' + ')
}
